import React from "react";
import { motion } from "framer-motion";

interface CoreValue {
  title: string;
  tagline: string;
  description: string;
}

interface OperationalMetric {
  value: string;
  label: string;
  subtext: string;
}

const coreValues: CoreValue[] = [
  {
    title: "Industrial Precision Quality",
    tagline: "Strict Batch-Control Standards",
    description: "We enforce strict biosecurity and cleanroom controls to maintain zero-compromise physical consistency. Every bulk batch of Fresh Button and Dried Oyster mushrooms matches rigorous phytosanitary standards for flawless processing integration.",
  },
  {
    title: "Agricultural Scale & Infrastructure",
    tagline: "Uninterrupted Supply Chains",
    description: "Through advanced commercial facilities and an extensive contract farming network across Maharashtra, we provide pharmaceutical, food processing, and wholesale partners with multi-ton security and reliable contractual supply schedules.",
  },
  {
    title: "Socio-Economic Ecosystem Development",
    tagline: "Empowering Rural Agritech",
    description: "Our infrastructure builds direct economic value. By equipping more than 1,000+ students and regional cultivators with modern agricultural skillsets and guaranteed institutional buybacks, we build robust, future-proof rural farming communities.",
  },
  {
    title: "Compliant Export Sustainability",
    tagline: "Eco-Conscious Agro-Farming",
    description: "We optimize resource workflows by turning agricultural upcycled substrate waste into rich bioproductive inputs, ensuring our industrial methods leave a minimized carbon footprint while fully aligning with international green import mandates.",
  },
];

const operationalMetrics: OperationalMetric[] = [
  {
    value: "1,000+",
    label: "Agritech Students Trained",
    subtext: "Empowered commercial mushroom cultivators and micro-entrepreneurs across Maharashtra.",
  },
  {
    value: "Multi-Ton",
    label: "Wholesale Supply Capacity",
    subtext: "Consistent logistics channels delivering high-volume Fresh Button & Dried Oyster orders.",
  },
  {
    value: "100%",
    label: "Turnkey Contract Integration",
    subtext: "From commercial laboratory environment setups and spawn provisioning to final buyback.",
  },
  {
    value: "Global",
    label: "Export Standards Met",
    subtext: "Dried Oyster variants processed to match strict international phytosanitary compliance.",
  },
];

const OurCoreValues: React.FC = () => {
  return (
    <section className="py-24 bg-neutral-900 text-white relative overflow-hidden">
      {/* Premium subtle technical background pattern */}
      <div className="absolute inset-0 opacity-5 bg-[radial-gradient(#10b981_1px,transparent_1px)] [background-size:16px_16px]" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        {/* Core Values Section Block */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="text-xs font-mono font-bold tracking-widest text-emerald-400 uppercase bg-emerald-950/60 px-4 py-1.5 rounded-full border border-emerald-800/40">
            Corporate Pillars
          </span>
          <h2 className="text-3xl md:text-5xl font-bold font-serif tracking-tight mt-4 text-white">
            Our Core Foundations
          </h2>
          <div className="w-16 h-1 bg-emerald-500 mx-auto mt-6 rounded" />
        </div>

        {/* 2x2 Clean Grid Layout for B2B Values */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-24">
          {coreValues.map((value, idx) => (
            <motion.article
              key={idx}
              className="p-8 rounded-2xl bg-neutral-800/40 border border-neutral-800/80 hover:border-emerald-500/30 transition-all duration-300 backdrop-blur-sm"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: idx * 0.1 }}
            >
              <div className="text-xs font-mono font-semibold text-emerald-400 uppercase tracking-wide mb-2">
                {value.tagline}
              </div>
              <h3 className="text-xl font-bold text-white tracking-wide mb-3">
                {value.title}
              </h3>
              <p className="text-neutral-400 text-sm md:text-base leading-relaxed text-justify">
                {value.description}
              </p>
            </motion.article>
          ))}
        </div>

        {/* Risk-Free Industrial Metric Grid Section (Replaced Testimonials) */}
        <div className="border-t border-neutral-800 pt-24">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold font-serif tracking-tight text-white">
              Operational Scale & Footprint
            </h3>
            <p className="text-neutral-400 text-sm mt-2">
              Verified metrics powering our domestic supply chains, training structures, and international trade lines.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {operationalMetrics.map((metric, index) => (
              <motion.div
                key={index}
                className="p-8 rounded-2xl bg-neutral-950/50 border border-neutral-800/60 text-center flex flex-col justify-between"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div>
                  <div className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-300 tracking-tight font-sans mb-3">
                    {metric.value}
                  </div>
                  <h4 className="text-base font-bold text-neutral-100 tracking-wide mb-2">
                    {metric.label}
                  </h4>
                </div>
                <p className="text-xs text-neutral-400 leading-relaxed mt-2">
                  {metric.subtext}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default OurCoreValues;